import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { BackListButton, ToastContext } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import useLocations from '@/common/hooks/useLocations/useLocations'
import usePackaging from '@/common/hooks/usePackaging/usePackaging'
import ParcelInfoHeader from '@/parcels/components/ParcelInfoHeader/ParcelInfoHeader'
import { isInternationalAddresses } from '@/parcels/helpers/helpers'
import useAmendParcelItems from '@/parcels/hooks/useAmendParcelItems/useAmendParcelItems'
import useParcel from '@/parcels/hooks/useParcel/useParcel'
import usePopulatedCarrierServices from '@/parcels/hooks/usePopulatedCarrierServices/usePopulatedCarrierServices'
import usePrepareParcelItems from '@/parcels/hooks/usePrepareParcelItems/usePrepareParcelItems'
import routes from '@/Router/routes.constants'

import AmendItemsErrorModal from './AmendItemsErrorModal/AmendItemsErrorModal'
import PackProductsCard from './PackProductsCard/PackProductsCard'
import ParcelPreparePageSkeleton from './ParcelPreparePageSkeleton/ParcelPreparePageSkeleton'
import SummaryCard from './SummaryCard/SummaryCard'
import * as Styles from './styles'

const ParcelPreparePage = () => {
  const { id } = useParams()
  const { info } = useContext(AppContext)
  const { parcel, loading } = useParcel({ id, storeId: info?.selectedStore?.id })
  const { t } = useTranslation()
  const navigation = useNavigate()
  const { setToastInfo } = useContext(ToastContext)
  const { locations } = useLocations({ storeId: info?.selectedStore?.id })
  const { packaging, fetchingPackaging } = usePackaging({ storeId: info?.selectedStore?.id })
  const { carrierServices, fetchingCarriers } = usePopulatedCarrierServices({
    storeId: info?.selectedStore?.id,
    parcelCarrier: parcel?.related?.shipping_method?.carrier,
  })
  const {
    itemsToPrepare,
    changeItemStatus,
    onChangeProductCode,
    productCode,
    setProductCode,
    productCodeError,
    notes,
    handleChangeNotes,
    isParcelEnableToPrepare,
    prepareParcel,
    preparingParcel,
    discardAction,
    setDiscardAction,
    carrierServiceForNewShipment,
    setCarrierServiceForNewShipment,
    locationForNewShipment,
    setLocationForNewShipment,
    selectedPackaging,
    setSelectedPackaging,
  } = usePrepareParcelItems({
    items: parcel?.related?.line_items,
    storeId: info?.selectedStore?.id,
    parcelId: id,
    relatedId: parcel?.related?.id,
    navigation,
    setToastInfo,
    returnToParcelsPageAfterPrepare: info?.returnToParcelsPageAfterPrepare,
    carrierServiceId: parcel?.related?.shipping_method?.carrier?.id,
    locations,
    senderLocationId: parcel?.related?.sender_location?.id,
    packaging,
    parcel,
  })
  const { amendItems, amendingItems, showAmendItemsErrorModal, hasSomeItemsToAmend, closeAmendItemsErrorModal } =
    useAmendParcelItems({
      storeId: info?.selectedStore?.id,
      parcelId: id,
      relatedId: parcel?.related?.id,
      items: itemsToPrepare,
      navigation,
    })

  if (loading || fetchingCarriers || fetchingPackaging) return <ParcelPreparePageSkeleton />

  return (
    <Styles.Container>
      <div>
        <BackListButton to={routes.parcelDetailPage.replace(':id', id)}>{t('Volver al detalle')}</BackListButton>
      </div>
      <ParcelInfoHeader
        orderName={`${t('Preparar envío')} ${parcel?.order?.name}`}
        parcelType={parcel?.type}
        isExchange={parcel?.order?.is_exchange}
        isInternational={isInternationalAddresses(parcel?.origin, parcel?.destination)}
        withCustoms={parcel?.related?.with_customs}
        senderAddress={parcel?.origin}
        recipientAddress={parcel?.destination}
        showMoreActions={false}
      />
      <Styles.MainContainer>
        <PackProductsCard
          lineItems={itemsToPrepare}
          onChangeItemStatus={changeItemStatus}
          onChangeProductCode={onChangeProductCode}
          productCodeError={productCodeError}
          productCode={productCode}
          setProductCode={setProductCode}
          notes={parcel?.related?.customer_notes}
        />
        <div>
          <SummaryCard
            lineItems={itemsToPrepare}
            parcelId={id}
            notes={notes}
            handleChangeNotes={handleChangeNotes}
            isParcelEnableToPrepare={isParcelEnableToPrepare()}
            withCustoms={parcel?.related?.with_customs}
            prepareParcel={prepareParcel}
            preparingParcel={preparingParcel}
            carrierServices={carrierServices}
            discardAction={discardAction}
            setDiscardAction={setDiscardAction}
            carrierServiceForNewShipment={carrierServiceForNewShipment}
            setCarrierServiceForNewShipment={setCarrierServiceForNewShipment}
            locationForNewShipment={locationForNewShipment}
            setLocationForNewShipment={setLocationForNewShipment}
            senderLocation={parcel?.related?.sender_location}
            locations={locations}
            parcelStatus={parcel?.status}
            amendItems={amendItems}
            amendingItems={amendingItems}
            hasSomeItemsToAmend={hasSomeItemsToAmend}
            packaging={packaging}
            selectedPackaging={selectedPackaging}
            onChangeSelectedPackaging={setSelectedPackaging}
          />
        </div>
      </Styles.MainContainer>
      <AmendItemsErrorModal show={showAmendItemsErrorModal} onClose={closeAmendItemsErrorModal} />
    </Styles.Container>
  )
}

export default ParcelPreparePage
